import { useEffect, useState } from "react";

/**
 * 홈 화면 설치 — Android Chrome 의 beforeinstallprompt 를 잡아 두었다가 더보기의 "앱 설치"에서 띄운다.
 * 들어오자마자 설치창을 띄우지 않는다(토스·NN/g 가이드). iOS 는 이 이벤트가 없어 안내 문구만 보인다.
 * 이벤트는 화면이 뜨기 전에 올 수 있어 모듈을 불러오는 순간부터 듣는다(MobileShell 이 import).
 */
type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };
let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferred = e as InstallEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => { deferred = null; notify(); });
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function isIos(): boolean {
  return typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/** [설치 가능 여부, 설치창 띄우기] */
export function useInstallPrompt(): [boolean, () => Promise<void>] {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice.catch(() => undefined);
    deferred = null;
    notify();
  };
  return [Boolean(deferred), install];
}
